import { useEffect, useRef, useState } from "react";
import { Database, Upload, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Pannello importazione archivio storico LogiRent (solo titolare).
 * Flusso: upload .xlsx → anteprima → conferma → progress bar via polling.
 */
export default function LogiRentImportPanel() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [stats, setStats] = useState(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const loadStats = async () => {
    try {
      const r = await api.get("/logirent/stats");
      setStats(r.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadStats(); }, []);

  const doPreview = async () => {
    if (!file) { toast.error("Seleziona un file .xlsx"); return; }
    setUploading(true); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/logirent/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      toast.success(`Anteprima: ${r.data.total_rows} righe, ${r.data.nuovi} nuove`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore anteprima");
    } finally { setUploading(false); }
  };

  const confirm = async () => {
    if (!preview?.preview_id) return;
    try {
      const r = await api.post(`/logirent/confirm/${preview.preview_id}`);
      setJobId(r.data.job_id);
      setProgress({ status: "queued", percent: 0, processed: 0, total: preview.total_rows });
      pollRef.current = setInterval(async () => {
        try {
          const pr = await api.get(`/logirent/progress/${r.data.job_id}`);
          setProgress(pr.data);
          if (pr.data.status === "done" || pr.data.status === "error") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            loadStats();
            if (pr.data.status === "done") {
              toast.success(`Import completato: ${pr.data.inserted} nuovi contratti (${pr.data.skipped_existing} già presenti)`);
            } else {
              toast.error(pr.data.error || "Errore import");
            }
          }
        } catch (e) { /* keep polling */ }
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore conferma");
    }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const reset = () => {
    setFile(null); setPreview(null); setJobId(null); setProgress(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="logirent-import-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Database size={16} className="text-[#ff0033]" />
          <div className="font-display font-bold uppercase tracking-wider text-sm">Importa Archivio LogiRent</div>
        </div>
        {stats && (
          <div className="text-xs text-white/50 font-mono">
            archivio: <span className="text-white/90">{stats.contratti_totali}</span> contratti ·
            dismesse: <span className="text-white/90">{stats.veicoli_dismessi}</span> ·
            da abbinare: <span className="text-amber-400">{stats.da_abbinare}</span>
          </div>
        )}
      </div>
      <p className="text-xs text-white/45 mb-4">
        Archivio storico in sola lettura. Nessuna email/WhatsApp/pagamento, nessuna nuova numerazione. Idempotente su <code>id_origine</code>.
      </p>

      {!progress && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <input ref={fileRef} data-testid="logirent-file-input" type="file" accept=".xlsx,.xlsm"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              className="text-xs text-white/70 file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-white/[0.06] file:text-white/90 file:text-xs hover:file:bg-white/10" />
            <button data-testid="logirent-preview-btn" onClick={doPreview} disabled={!file || uploading}
              className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-40">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Anteprima
            </button>
          </div>

          {preview && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid="logirent-preview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Stat label="Righe totali" value={preview.total_rows} />
                <Stat label="Nuove" value={preview.nuovi} highlight />
                <Stat label="Già importate" value={preview.already_imported} />
                <Stat label="Clienti abbinati" value={preview.clienti_abbinati} />
                <Stat label="Clienti nuovi" value={preview.clienti_nuovi} />
                <Stat label="Da abbinare (omonimi)" value={preview.da_abbinare} warn={preview.da_abbinare > 0} />
                <Stat label="Targhe fuori flotta" value={preview.n_targhe_fuori_flotta} />
                <Stat label="Anomalie" value={preview.anomalie?.length || 0} warn={(preview.anomalie?.length || 0) > 0} />
              </div>

              {preview.per_anno && Object.keys(preview.per_anno).length > 0 && (
                <div className="text-xs">
                  <div className="text-white/50 uppercase tracking-wider mb-1">Per anno</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(preview.per_anno).map(([y, n]) => (
                      <span key={y} className="px-2 py-1 rounded-md bg-white/[0.04] border border-white/10">
                        {y}: <b>{n}</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {preview.per_stato && Object.keys(preview.per_stato).length > 0 && (
                <div className="text-xs">
                  <div className="text-white/50 uppercase tracking-wider mb-1">Per stato</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(preview.per_stato).map(([s, n]) => (
                      <span key={s} className="px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 capitalize">
                        {s}: <b>{n}</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {preview.anomalie && preview.anomalie.length > 0 && (
                <div className="text-xs">
                  <div className="flex items-center gap-1 text-amber-400 uppercase tracking-wider mb-1">
                    <AlertTriangle size={12} /> Anomalie (prime {preview.anomalie.length})
                  </div>
                  <ul className="list-disc list-inside text-white/60 max-h-40 overflow-auto">
                    {preview.anomalie.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={reset} className="px-4 py-2 rounded-full text-xs bg-white/[0.04] border border-white/10 hover:bg-white/10">
                  Annulla
                </button>
                <button data-testid="logirent-confirm-btn" onClick={confirm} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
                  <CheckCircle2 size={14} /> Conferma importazione
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {progress && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="logirent-progress">
          <div className="flex items-center justify-between mb-2 text-xs">
            <div className="text-white/60">
              Import in corso ({progress.status})
            </div>
            <div className="font-mono text-white/80">
              {progress.processed || 0} / {progress.total || 0} ({progress.percent || 0}%)
            </div>
          </div>
          <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
            <div className="h-full bg-[#ff0033] transition-all" style={{ width: `${progress.percent || 0}%` }} />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <Stat label="Inseriti" value={progress.inserted || 0} />
            <Stat label="Già presenti" value={progress.skipped_existing || 0} />
            <Stat label="Omonimi" value={progress.pending_matches || 0} warn={(progress.pending_matches || 0) > 0} />
          </div>
          {(progress.status === "done" || progress.status === "error") && (
            <div className="flex justify-end mt-3">
              <button onClick={reset} className="px-4 py-2 rounded-full text-xs bg-white/[0.04] border border-white/10 hover:bg-white/10">
                Chiudi
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight, warn }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${warn ? "border-amber-400/30 bg-amber-400/5" : highlight ? "border-[#ff0033]/40 bg-[#ff0033]/5" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className={`text-base font-bold ${warn ? "text-amber-300" : highlight ? "text-[#ff0033]" : "text-white"}`}>{value}</div>
    </div>
  );
}
